import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, Shield, Clock, Award, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import Footer from './components/Footer';
import { useLoanCalculator } from './hooks/useLoanCalculator';
import { useFormValidation } from './hooks/useFormValidation';
import { LOAN_CONFIG, CONTACT } from './config/constants';

// ─── EmailJS config ───────────────────────────────────────────────────────────
// 1. Crée un compte sur https://emailjs.com (gratuit)
// 2. Ajoute un service Gmail, note le SERVICE_ID
// 3. Crée un template avec les variables {{from_name}}, {{message}}, etc. Note le TEMPLATE_ID
// 4. Note ta PUBLIC_KEY (anciennement User ID)
// 5. Remplace les 3 valeurs ci-dessous :
const EMAILJS_SERVICE_ID  = 'service_m2nlh6l';
const EMAILJS_TEMPLATE_ID = 'template_gjs6b78';
const EMAILJS_PUBLIC_KEY  = 'vTjXRpt3p_AOOHCI0';
// ─────────────────────────────────────────────────────────────────────────────

const DonexaPlatform = () => {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState(15000);
  const [duration, setDuration] = useState(24);
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    income: '',
    employment: ''
  });

  const { monthlyPayment, totalAmount, formatEuro, formatDuration } = useLoanCalculator(amount, duration);
  const { validationErrors, validateForm, isFormValid, clearError, formatPhoneNumber } = useFormValidation(t, i18n.language);

  // Prevent spacebar from scrolling
  useEffect(() => {
    const preventSpaceScroll = (e) => {
      if (e.code === 'Space' && e.target === document.body) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', preventSpaceScroll);
    return () => window.removeEventListener('keydown', preventSpaceScroll);
  }, []);

  // useCallback stabilise la référence pour éviter les re-renders intempestifs
  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      clearError(name);
    }
  }, [validationErrors, clearError]);

  const getFinalReason = () => {
    if (reason === 'other') return customReason;
    const reasonMap = {
      debtConsolidation: t('reasonDebtConsolidation'),
      homeBuying: t('reasonHomeBuying'),
      homeRenovation: t('reasonHomeRenovation'),
      car: t('reasonCar'),
      education: t('reasonEducation'),
      wedding: t('reasonWedding'),
      travel: t('reasonTravel'),
      medical: t('reasonMedical'),
      business: t('reasonBusiness')
    };
    return reasonMap[reason] || '';
  };

  // ─── Envoi email via EmailJS ────────────────────────────────────────────────
  const sendEmailJS = async (templateParams) => {
    // Chargement dynamique du SDK EmailJS
    if (!window.emailjs) {
      await new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
      window.emailjs.init(EMAILJS_PUBLIC_KEY);
    }
    return window.emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams);
  };

  const handleSubmit = async () => {
    if (!validateForm(formData)) return;

    const finalReason = getFinalReason();
    const formattedPhone = formatPhoneNumber(formData.phone);

    const messageText = `Nouvelle demande de prêt / Uusi lainahakemus

Nom / Nimi: ${formData.firstName} ${formData.lastName}
Ville / Kaupunki: ${formData.city || '—'}

Montant / Summa: ${formatEuro(amount)}
Durée / Kesto: ${formatDuration}
Mensualité / Kuukausierä: ${formatEuro(monthlyPayment)}
Total / Kokonaissumma: ${formatEuro(totalAmount)}
Objet / Käyttötarkoitus: ${finalReason}

Contact:
Email: ${formData.email}
Téléphone / Puhelin: ${formattedPhone}

Situation financière / Taloudellinen tilanne:
Revenus / Tulot: ${formData.income} €/mois
Emploi / Työtilanne: ${formData.employment}`;

    setIsSending(true);

    // ── 1. Envoi email ─────────────────────────────────────────────────────
    try {
      await sendEmailJS({
        to_email:  CONTACT.SUPPORT_EMAIL,
        from_name: `${formData.firstName} ${formData.lastName}`,
        reply_to:  formData.email,
        subject:   `Nouvelle demande de prêt – ${formatEuro(amount)}`,
        message:   messageText,
      });
    } catch (emailError) {
      console.error('EmailJS error:', emailError);
      // On continue quand même vers WhatsApp même si l'email échoue
    }

    // ── 2. Événement Meta Pixel Lead ───────────────────────────────────────
    if (window.fbq) {
      window.fbq('track', 'Lead', {
        value: amount,
        currency: 'EUR',
        content_name: 'Loan Application',
      });
    }

    setIsSending(false);
    setStep(5);
  };

  const heroBackgroundStyle = {
    backgroundImage: 'linear-gradient(rgba(16, 185, 129, 0.6), rgba(20, 184, 166, 0.6)), url("https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=1600&q=80")',
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  };

  // ─── STEP 1 ────────────────────────────────────────────────────────────────
  // NOTE : Les étapes sont rendues directement en JSX (pas comme sous-composants)
  // pour éviter que React démonte/remonte les inputs à chaque re-render,
  // ce qui causait la perte du focus après chaque frappe.

  const renderStep1 = () => (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={heroBackgroundStyle}>
      <LanguageSelector />
      <div className="max-w-2xl w-full">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg" className="drop-shadow-xl">
              <rect width="52" height="52" rx="14" fill="url(#logoGrad)"/>
              <path d="M16 14 L16 36 L36 36" stroke="white" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="36" cy="36" r="4" fill="white"/>
              <path d="M22 26 L28 20 L34 26" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.7"/>
              <defs>
                <linearGradient id="logoGrad" x1="0" y1="0" x2="52" y2="52" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#f59e0b"/>
                  <stop offset="100%" stopColor="#b45309"/>
                </linearGradient>
              </defs>
            </svg>
            <div className="ml-3">
              <span className="text-3xl font-bold text-white drop-shadow-lg tracking-tight">Luotto<span style={{color:'#fcd34d'}}>laina</span></span>
              <div className="text-xs text-white/70 tracking-widest uppercase font-medium">Lainapalvelu</div>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            {t('title')}
          </h1>
          <p className="text-xl text-white drop-shadow-md">
            {t('subtitle')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <div className="mb-8">
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              {t('howMuch')}
            </label>
            <div className="text-center mb-6">
              <div className="text-5xl font-bold text-emerald-600">
                {formatEuro(amount)}
              </div>
            </div>
            <input
              type="range"
              min={LOAN_CONFIG.MIN_AMOUNT}
              max={LOAN_CONFIG.MAX_AMOUNT}
              step={LOAN_CONFIG.AMOUNT_STEP}
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value))}
              className="w-full h-3 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
            />
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>{formatEuro(LOAN_CONFIG.MIN_AMOUNT)}</span>
              <span>{formatEuro(LOAN_CONFIG.MAX_AMOUNT)}</span>
            </div>
          </div>

          <button
            onClick={() => {
              if (window.fbq) window.fbq('track', 'InitiateCheckout', { value: amount, currency: 'EUR' });
              setStep(2);
            }}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-4 rounded-xl font-semibold text-lg hover:from-emerald-700 hover:to-teal-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
          >
            {t('continue')}
            <ChevronRight size={24} />
          </button>

          <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t">
            <div className="text-center">
              <Shield className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-gray-600">{t('secure')}</p>
            </div>
            <div className="text-center">
              <Clock className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-gray-600">{t('fastResponse')}</p>
            </div>
            <div className="text-center">
              <Award className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-xs text-gray-600">{t('fixedRate')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── STEP 2 ────────────────────────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4 relative">
      <LanguageSelector />
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">
              {t('chooseDuration')}
            </h2>
            <p className="text-gray-600">
              {t('loanOf')} {formatEuro(amount)}
            </p>
          </div>

          <div className="mb-8">
            <div className="text-center mb-6">
              <div className="text-4xl font-bold text-emerald-600">
                {formatDuration}
              </div>
            </div>
            <input
              type="range"
              min={LOAN_CONFIG.MIN_DURATION}
              max={LOAN_CONFIG.MAX_DURATION}
              step={LOAN_CONFIG.DURATION_STEP}
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value))}
              className="w-full h-3 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
            />
            <div className="flex justify-between text-sm text-gray-500 mt-2">
              <span>{LOAN_CONFIG.MIN_DURATION} {t('months')}</span>
              <span>{LOAN_CONFIG.MAX_DURATION / 12} {t('years')}</span>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-lg font-semibold text-gray-700 mb-4">
              {t('loanReason')}
            </label>
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setCustomReason('');
              }}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
            >
              <option value="">{t('selectReason')}</option>
              <option value="debtConsolidation">{t('reasonDebtConsolidation')}</option>
              <option value="homeBuying">{t('reasonHomeBuying')}</option>
              <option value="homeRenovation">{t('reasonHomeRenovation')}</option>
              <option value="car">{t('reasonCar')}</option>
              <option value="education">{t('reasonEducation')}</option>
              <option value="wedding">{t('reasonWedding')}</option>
              <option value="travel">{t('reasonTravel')}</option>
              <option value="medical">{t('reasonMedical')}</option>
              <option value="business">{t('reasonBusiness')}</option>
              <option value="other">{t('reasonOther')}</option>
            </select>

            {reason === 'other' && (
              <input
                type="text"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder={t('writeReason')}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent mt-4"
              />
            )}
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 mb-8">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('monthly')}</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {formatEuro(monthlyPayment)}{t('perMonth')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">{t('totalAmount')}</p>
                <p className="text-2xl font-bold text-gray-800">
                  {formatEuro(totalAmount)}
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-emerald-200">
              <p className="text-sm text-gray-600">
                {t('interestRate')}: <span className="font-semibold">3,00 % / {t('yearUnit')}</span>
              </p>
            </div>
          </div>

          <button
            onClick={() => setStep(3)}
            disabled={!reason || (reason === 'other' && !customReason.trim())}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
              reason && (reason !== 'other' || customReason.trim())
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {t('continue')}
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </div>
  );

  // ─── STEP 3 ────────────────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4 relative">
      <LanguageSelector />
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            {t('summary')}
          </h2>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-8 mb-8">
            <div className="space-y-4">
              <div className="flex justify-between items-center pb-4 border-b border-emerald-200">
                <span className="text-gray-600">{t('requestedAmount')}</span>
                <span className="text-2xl font-bold text-gray-800">{formatEuro(amount)}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-emerald-200">
                <span className="text-gray-600">{t('term')}</span>
                <span className="text-xl font-semibold text-gray-800">{formatDuration}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-emerald-200">
                <span className="text-gray-600">{t('purpose')}</span>
                <span className="text-base font-medium text-gray-800">{getFinalReason()}</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-emerald-200">
                <span className="text-gray-600">{t('interestRate')}</span>
                <span className="text-xl font-semibold text-gray-800">3,00 %</span>
              </div>
              <div className="flex justify-between items-center pb-4 border-b border-emerald-200">
                <span className="text-gray-600">{t('monthly')}</span>
                <span className="text-2xl font-bold text-emerald-600">{formatEuro(monthlyPayment)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">{t('totalAmount')}</span>
                <span className="text-xl font-semibold text-gray-800">{formatEuro(totalAmount)}</span>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
              />
              <span className="text-sm text-gray-700">
                {t('termsAccept')}
              </span>
            </label>
          </div>

          <button
            onClick={() => setStep(4)}
            disabled={!acceptedTerms}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
              acceptedTerms
                ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {t('proceedApplication')}
            <ChevronRight size={24} />
          </button>
        </div>
      </div>
    </div>
  );

  // ─── STEP 4 ────────────────────────────────────────────────────────────────
  const renderStep4 = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4 relative">
      <LanguageSelector />
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-2 text-center">
            {t('yourInfo')}
          </h2>
          <p className="text-center text-gray-600 mb-8">
            {t('lastStep')}
          </p>

          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('firstName')} *
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleFormChange}
                  autoComplete="given-name"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                    validationErrors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('lastName')} *
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleFormChange}
                  autoComplete="family-name"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                    validationErrors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {validationErrors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('email')} *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleFormChange}
                autoComplete="email"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  validationErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.email && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('phone')} *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleFormChange}
                placeholder="+358 40 123 4567"
                autoComplete="tel"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.phone && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('city')} *
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleFormChange}
                autoComplete="address-level2"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  validationErrors.city ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.city && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.city}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('monthlyIncome')} *
              </label>
              <input
                type="text"
                name="income"
                value={formData.income}
                onChange={handleFormChange}
                placeholder="Ex: 3000"
                inputMode="numeric"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  validationErrors.income ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {validationErrors.income && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.income}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('employment')} *
              </label>
              <select
                name="employment"
                value={formData.employment}
                onChange={handleFormChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                  validationErrors.employment ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('select')}</option>
                <option value="cdi">{t('employmentCDI')}</option>
                <option value="cdd">{t('employmentCDD')}</option>
                <option value="independant">{t('employmentSelfEmployed')}</option>
                <option value="fonctionnaire">{t('employmentCivilServant')}</option>
                <option value="retraite">{t('employmentRetired')}</option>
                <option value="autre">{t('employmentOther')}</option>
              </select>
              {validationErrors.employment && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.employment}</p>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={!isFormValid(formData) || isSending}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                isFormValid(formData) && !isSending
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSending ? '...' : t('sendApplication')}
              <CheckCircle size={24} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── STEP 5 ────────────────────────────────────────────────────────────────
  const renderStep5 = () => (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4 relative">
      <LanguageSelector />
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={48} className="text-emerald-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            {t('successTitle')}
          </h2>

          <p className="text-gray-600 mb-8">
            {t('successMessage')} {formatEuro(amount)} {t('successMessage2')}
          </p>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6 mb-8">
            <p className="text-sm text-gray-700">
              {t('confirmationEmail')} <strong>{formData.email}</strong>
            </p>
          </div>

          <p className="text-sm text-gray-500">
            {t('responseTime')}
          </p>
        </div>
      </div>
    </div>
  );

  // ─── Témoignages ─────────────────────────────────────────────────────────
  // ─── Témoignages ─────────────────────────────────────────────────────────
  const [userReviews, setUserReviews] = React.useState([]);
  const [reviewForm, setReviewForm] = React.useState({ name: '', city: '', text: '', stars: 5 });
  const [reviewSubmitted, setReviewSubmitted] = React.useState(false);

  const staticTestimonials = [
    { name: "Mikko Virtanen",    city: "Helsinki",    stars: 5, text: "Sain lainan todella nopeasti ja korko oli juuri niin kuin luvattiin. Erittäin sujuva prosessi!", hasPhoto: true },
    { name: "Maria Nieminen",    city: "Tampere",     stars: 5, text: "Erinomainen palvelu! Kuukausierä on täsmälleen se mitä laskurissa näkyi. Suosittelen lämpimästi.", hasPhoto: true },
    { name: "Jari Mäkinen",      city: "Turku",       stars: 5, text: "Käytin lainaa kodin remontointiin. Hakemus oli helppo täyttää ja vastaus tuli saman päivän aikana.", hasPhoto: false },
    { name: "Sofia Hämäläinen",  city: "Oulu",        stars: 5, text: "Selkeä laskuri ja rehellinen korko. Ei piilomaksuja. Juuri sellainen palvelu kuin etsin!", hasPhoto: true },
    { name: "Timo Koskinen",     city: "Jyväskylä",   stars: 5, text: "Halusin ostaa auton ja tarvitsin nopeasti lainaa. Luottolaina hoiti kaiken vaivattomasti.", hasPhoto: false },
    { name: "Aino Heikkinen",    city: "Lahti",       stars: 5, text: "Prosessi oli niin yksinkertainen. Täytin lomakkeen ja sain vastauksen muutamassa tunnissa.", hasPhoto: true },
    { name: "Marko Järvinen",    city: "Espoo",       stars: 5, text: "Olen käyttänyt monia lainapalveluita, mutta Luottolaina on selkeästi paras. Kiinteä korko on iso plussa.", hasPhoto: false },
    { name: "Linnea Turunen",    city: "Vantaa",      stars: 5, text: "Luottolaina auttoi minua yhdistämään vanhat lainani yhdeksi. Säästän nyt 200€ kuukaudessa!", hasPhoto: true },
    { name: "Olavi Kinnunen",    city: "Kuopio",      stars: 5, text: "Asiakaspalvelu vastasi kaikkiin kysymyksiini nopeasti. Erittäin ammattimainen toiminta.", hasPhoto: false },
    { name: "Kaisa Virtanen",    city: "Pori",        stars: 5, text: "Käytin lainaa koulutukseen. Ehdot olivat selkeät ja kuukausierä sopii budjettiini täydellisesti.", hasPhoto: true },
    { name: "Eetu Nieminen",     city: "Helsinki",    stars: 5, text: "Nopea, luotettava ja läpinäkyvä. Suosittelen kaikille jotka tarvitsevat lainaa ilman yllätyksiä.", hasPhoto: false },
    { name: "Aurora Mäkinen",    city: "Tampere",     stars: 5, text: "Kiinteä 3% korko on todella kilpailukykyinen. Olen tyytyväinen päätökseeni valita Luottolaina.", hasPhoto: true },
    { name: "Juha Heikkinen",    city: "Turku",       stars: 5, text: "Hakemus valmistui alle 10 minuutissa. Vastaus samana päivänä. Täydellistä palvelua!", hasPhoto: false },
    { name: "Anneli Koskinen",   city: "Oulu",        stars: 5, text: "Käytin lainaa hääjuhlan järjestämiseen. Prosessi sujui mutkattomasti ja yhteistyö oli miellyttävää.", hasPhoto: true },
    { name: "Kai Järvinen",      city: "Jyväskylä",   stars: 5, text: "Lainasumman valinta laskurilla oli helppoa. Näin heti mitä maksan kuukausittain. Erittäin selkeää!", hasPhoto: false },
    { name: "Minna Turunen",     city: "Lahti",       stars: 5, text: "Luottolaina on rehellinen palvelu. Ei piilokustannuksia, ei yllätyksiä. Juuri sitä mitä lupattiin.", hasPhoto: true },
    { name: "Ilmari Kinnunen",   city: "Espoo",       stars: 5, text: "Tarvitsin lainaa liiketoimintaani varten. Sain hyvän summan nopeasti. Erittäin tyytyväinen!", hasPhoto: false },
    { name: "Sanna Virtanen",    city: "Vantaa",      stars: 5, text: "Olen suositellut Luottolainaa jo usealle ystävälleni. Palvelu on yksinkertaisesti paras markkinoilla.", hasPhoto: true },
    { name: "Aapeli Nieminen",   city: "Kuopio",      stars: 5, text: "Kodin remontti onnistui lainan avulla täydellisesti. Kuukausierä oli juuri sopiva tuloihini nähden.", hasPhoto: false },
    { name: "Olivia Hämäläinen", city: "Pori",        stars: 5, text: "Hakemus oli selkeä ja ymmärrettävä. Sain rahat tililleni erittäin nopeasti. Suosittelen!", hasPhoto: true },
    { name: "Urho Mäkinen",      city: "Helsinki",    stars: 5, text: "Vertailin monia palveluita ja Luottolaina oli selvästi paras vaihtoehto. Kiinteä korko ratkaisi asian.", hasPhoto: false },
    { name: "Tuulikki Koskinen", city: "Tampere",     stars: 5, text: "Olen erittäin tyytyväinen saamaani palveluun. Luottolainan tiimi on ammattitaitoinen ja ystävällinen.", hasPhoto: true },
    { name: "Anna Järvinen",     city: "Turku",       stars: 5, text: "Selkeä ja nopea palvelu. Suosittelen kaikille jotka etsivät luotettavaa lainavälittäjää Suomessa.", hasPhoto: true },
    { name: "Toivo Heikkinen",   city: "Oulu",        stars: 5, text: "Erinomainen kokemus alusta loppuun. Laskuri on tarkka ja hakemusprosessi todella vaivaton.", hasPhoto: false },
    { name: "Kaisu Turunen",     city: "Jyväskylä",   stars: 5, text: "Käytin palvelua ensimmäistä kertaa ja olin positiivisesti yllättynyt. Erittäin suositeltava!", hasPhoto: true },
    { name: "Matti Kinnunen",    city: "Lahti",       stars: 5, text: "Sain lainan nopeasti ja ehdot olivat täsmälleen niin kuin luvattiin. Ei turhia odotteluja.", hasPhoto: false },
    { name: "Anne Virtanen",     city: "Espoo",       stars: 5, text: "Luottolaina auttoi minua hankkimaan uuden auton. Prosessi oli helppo ja nopea. Kiitos!", hasPhoto: true },
    { name: "Aatos Nieminen",    city: "Vantaa",      stars: 4, text: "Hyvä palvelu ja nopea vastaus. Pieni parannus hakemusprosessiin voisi tehdä siitä vieläkin paremman.", hasPhoto: false },
    { name: "Olivia Mäkinen",    city: "Kuopio",      stars: 5, text: "Olen käyttänyt Luottolainaa kahdesti. Molemmat kerrat ovat sujuneet täydellisesti. Suosittelen!", hasPhoto: true },
    { name: "Risto Hämäläinen",  city: "Pori",        stars: 5, text: "Ammattimainen ja luotettava palvelu. Kiinteä korko antaa mielenrauhan koko laina-ajalle.", hasPhoto: false },
  ];

  const allTestimonials = [...staticTestimonials, ...userReviews];

  const getAvatarUrl = (name) =>
    `https://api.dicebear.com/7.x/personas/svg?seed=${encodeURIComponent(name)}&backgroundColor=fcd34d,fbbf24,f59e0b&radius=50`;

  const getInitials = (name) =>
    name.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const StarPicker = () => (
    <div className="flex gap-1">
      {[1,2,3,4,5].map(s => (
        <button key={s} type="button" onClick={() => setReviewForm(f => ({...f, stars: s}))}>
          <svg className={`w-6 h-6 ${s <= reviewForm.stars ? 'text-amber-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </button>
      ))}
    </div>
  );

  const handleReviewSubmit = () => {
    if (!reviewForm.name.trim() || !reviewForm.text.trim()) return;
    setUserReviews(prev => [{
      name: reviewForm.name.trim(),
      city: reviewForm.city.trim() || 'Suomi',
      stars: reviewForm.stars,
      text: reviewForm.text.trim(),
      hasPhoto: true,
      isNew: true
    }, ...prev]);
    setReviewForm({ name: '', city: '', text: '', stars: 5 });
    setReviewSubmitted(true);
    setTimeout(() => setReviewSubmitted(false), 4000);
  };

  const StarDisplay = ({ count }) => (
    <div className="flex gap-0.5">
      {[...Array(count)].map((_, s) => (
        <svg key={s} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
        </svg>
      ))}
    </div>
  );

  const renderTestimonials = () => (
    <div className="bg-white py-16 px-4">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-1">Mitä asiakkaamme sanovat</h2>
        <p className="text-center text-gray-500 mb-10 text-sm">Yli 500 tyytyväistä asiakasta</p>

        <div className="grid md:grid-cols-3 gap-4 mb-14">
          {allTestimonials.map((item, i) => (
            <div key={i} className={`rounded-2xl p-5 shadow-sm border hover:shadow-md transition-shadow ${item.isNew ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-100'}`}>
              <div className="flex items-center gap-3 mb-3">
                {item.hasPhoto ? (
                  <img
                    src={getAvatarUrl(item.name)}
                    alt={item.name}
                    className="w-10 h-10 rounded-full shadow flex-shrink-0 bg-amber-100"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-white font-bold text-xs shadow flex-shrink-0">
                    {getInitials(item.name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 text-sm truncate">{item.name}</div>
                  <div className="text-xs text-gray-500">{item.city}</div>
                </div>
                <div className="flex-shrink-0">
                  <StarDisplay count={item.stars} />
                </div>
              </div>
              <p className="text-gray-600 text-xs leading-relaxed">"{item.text}"</p>
              {item.isNew && <span className="inline-block mt-2 text-xs text-emerald-600 font-medium">✓ Uusi arvostelu</span>}
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100 max-w-2xl mx-auto">
          <h3 className="text-lg font-bold text-gray-800 mb-1">Jätä oma arvosteluasi 📝</h3>
          <p className="text-sm text-gray-500 mb-6">Oletko käyttänyt Luottolainaa? Kerro kokemuksestasi muille asiakkaille.</p>
          {reviewSubmitted ? (
            <div className="text-center py-6">
              <div className="text-4xl mb-3">🎉</div>
              <p className="text-emerald-700 font-semibold">Kiitos arvosteluasi! Se on nyt näkyvissä.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nimi *</label>
                  <input type="text" value={reviewForm.name} onChange={e => setReviewForm(f => ({...f, name: e.target.value}))} placeholder="Etunimi Sukunimi" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Kaupunki</label>
                  <input type="text" value={reviewForm.city} onChange={e => setReviewForm(f => ({...f, city: e.target.value}))} placeholder="Helsinki" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Arvosana *</label>
                <StarPicker />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Kommentti *</label>
                <textarea value={reviewForm.text} onChange={e => setReviewForm(f => ({...f, text: e.target.value}))} placeholder="Kerro kokemuksestasi Luottolainasta..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none" />
              </div>
              <button onClick={handleReviewSubmit} disabled={!reviewForm.name.trim() || !reviewForm.text.trim()} className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${reviewForm.name.trim() && reviewForm.text.trim() ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                Lähetä arvostelu ✓
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ─── Badges de sécurité ───────────────────────────────────────────────────
  const renderSecurityBadges = () => (
    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 py-10 px-4 border-t border-emerald-100">
      <div className="max-w-4xl mx-auto">
        <p className="text-center text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Turvallisuus & Luotettavuus</p>
        <div className="flex flex-wrap justify-center gap-4 md:gap-8">
          {[
            { icon: "🔒", label: "SSL-salattu", sub: "256-bit suojaus" },
            { icon: "🏦", label: "Rekisteröity välittäjä", sub: "Finanssivalvonta" },
            { icon: "✅", label: "GDPR-yhteensopiva", sub: "Tietosi ovat turvassa" },
            { icon: "⚡", label: "Nopea käsittely", sub: "Vastaus 24h sisällä" },
            { icon: "🇫🇮", label: "Suomalainen palvelu", sub: "Tehty Suomessa" },
          ].map((b, i) => (
            <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-sm border border-emerald-100 min-w-[160px]">
              <span className="text-2xl">{b.icon}</span>
              <div>
                <div className="text-xs font-semibold text-gray-800">{b.label}</div>
                <div className="text-xs text-gray-500">{b.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ─── Mentions légales ─────────────────────────────────────────────────────
  const renderLegal = () => (
    <div id="mentions" className="bg-gray-900 text-gray-400 py-12 px-4">
      <div className="max-w-4xl mx-auto space-y-8 text-xs leading-relaxed">
        <div>
          <h3 className="text-white font-semibold text-sm mb-2">Oikeudelliset tiedot</h3>
          <p>Luottolaina on lainavälityspalvelu, joka ei ole pankki eikä luottolaitos. Palvelu toimii välittäjänä asiakkaiden ja rahoittajien välillä. Kaikki lainapäätökset tekee rahoittaja, ei Luottolaina. Palvelu on suunnattu täysi-ikäisille henkilöille, joilla on vakituinen asuinpaikka Suomessa.</p>
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm mb-2">Vastuuvapauslauseke</h3>
          <p>Laskurin antamat tiedot ovat suuntaa-antavia. Lopullinen korko ja lainaehdot määräytyvät rahoittajan tekemän luottoarvioinnin perusteella. Kiinteä 3 % vuosikorko on esimerkki eikä takaa todellista korkoa.</p>
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm mb-2">Tietosuoja</h3>
          <p>Käsittelemme henkilötietojasi EU:n yleisen tietosuoja-asetuksen (GDPR) mukaisesti. Tietojasi käytetään ainoastaan lainahakemuksesi käsittelyyn. Emme myy tietojasi kolmansille osapuolille. Sinulla on oikeus pyytää tietojesi poistamista lähettämällä sähköpostia osoitteeseen juhanenonen2@gmail.com.</p>
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm mb-2">Yhteystiedot</h3>
          <p>Luottolaina • Sähköposti: juhanenonen2@gmail.com • Puhelin/WhatsApp: +358 46 962 5279</p>
        </div>
      </div>
    </div>
  );

  const showFooter = step === 1 || step === 5;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow">
        {step === 1 && renderStep1()}
        {step === 1 && renderTestimonials()}
        {step === 1 && renderSecurityBadges()}
        {step === 1 && renderLegal()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
        {step === 5 && renderStep5()}
      </div>
      {showFooter && <Footer />}
    </div>
  );
};

export default DonexaPlatform;